import { createElement, forwardRef, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { mergeProps } from '../../utils/with-default-props';
import Big from 'big.js';
import './index.scss';
const defaultProps = {
    defaultValue: 0,
    step: 1,
    disabled: false,
    onChange: () => { },
    onFocus: () => { },
    onBlur: () => { },
};
const ColorfulStepper = forwardRef((p, ref) => {
    const props = mergeProps(defaultProps, p);
    const { defaultValue, disabled, step, max, min, onChange, onFocus, onBlur } = props;
    const prefixCls = 'au-stepper';
    const [value, setValue] = useState(() => bound(defaultValue, min, max));
    const [inputValue, setInputValue] = useState(() => convertValueToText(value, props.digits));
    function setValueWithCheck(v) {
        if (isNaN(v))
            return;
        let target = bound(v, props.min, props.max);
        if (props.digits !== undefined) {
            target = parseFloat(target.toFixed(props.digits));
        }
        onChange?.(target);
        setValue(target);
    }
    const [hasFocus, setHasFocus] = useState(false);
    useEffect(() => {
        if (!hasFocus) {
            setInputValue(convertValueToText(value, props.digits));
        }
    }, [hasFocus]);
    useEffect(() => {
        if (!hasFocus) {
            setInputValue(convertValueToText(value, props.digits));
        }
    }, [value, props.digits]);
    const handleInputChange = v => {
        setInputValue(v);
        const val = convertTextToValue(v);
        if (val === null) {
            setValue(props.defaultValue);
        }
        else {
            setValueWithCheck(val);
        }
    };
    const handleMinus = (e) => {
        setValueWithCheck(Big(value !== null ? value : 0).minus(step).toNumber());
    };
    const handlePlus = (e) => {
        setValueWithCheck(Big(value !== null ? value : 0).add(step).toNumber());
    };
    const minusDisabled = () => {
        if (disabled)
            return true;
        if (value === null)
            return false;
        if (min !== undefined) {
            return value <= min;
        }
        return false;
    };
    const plusDisabled = () => {
        if (disabled)
            return true;
        if (value === null)
            return false;
        if (max !== undefined) {
            return value >= max;
        }
        return false;
    };
    const inputRef = useRef(null);
    return (createElement("div", { ...props, className: classNames(prefixCls) },
        createElement("button", { className: classNames('au-btn', `${prefixCls}-minus`), disabled: minusDisabled(), onClick: handleMinus, type: "button" },
            createElement("i", { className: "icon-minus" })),
        createElement("div", { className: classNames(`${prefixCls}-middle`) },
            createElement("div", { className: `au-input ${prefixCls}-input` },
                createElement("input", { className: "au-input-element", value: inputValue, ref: ref || inputRef, onChange: e => handleInputChange(e.target.value), onFocus: e => {
                        setHasFocus(true);
                        onFocus(e);
                    }, onBlur: e => {
                        setHasFocus(false);
                        onBlur(e);
                    } }))),
        createElement("button", { className: classNames('au-btn', `${prefixCls}-plus`), disabled: plusDisabled(), onClick: handlePlus, type: "button" },
            createElement("i", { className: "icon-plus" }))));
});
export function bound(value, min, max) {
    let ret = value;
    if (min !== undefined) {
        ret = Math.max(value, min);
    }
    if (max !== undefined) {
        ret = Math.min(ret, max);
    }
    return ret;
}
function convertValueToText(value, digits) {
    if (value === null)
        return '';
    if (digits !== undefined) {
        return value.toFixed(digits);
    }
    else {
        return value.toString();
    }
}
function convertTextToValue(text) {
    if (text === '')
        return null;
    return parseFloat(text);
}
ColorfulStepper.displayName = 'ColorfulStepper';
export default ColorfulStepper;
